import React, { useEffect, useState } from "react";
import { Select, Col, Row } from "antd";
import { Controller, useForm } from "react-hook-form";
import styles from "./add-edit-terms.module.css";
import CustomDrawer from "../../../../../../../../../common/custom-drawer/custom-drawer";
import CustomTextfield from "../../../../../../../../../common/input/custom-textfield/custom-textfield";
import CustomButton from "../../../../../../../../../common/custom-button/custom-button";
import CustomDatepicker from "../../../../../../../../../common/input/custom-datepicker/custom-datepicker";
import { ArrowRightOutlined } from "@ant-design/icons";
import CustomSelect from "../../../../../../../../../common/input/custom-select/custom-select";
import CustomCheckBox from "../../../../../../../../../common/custom-checkbox/custom-checkbox";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  createTerms,
  updateTerms,
} from "../../../../../../../../../services/school_info";
import { validateDate } from "../../../../../../../../../utils/format-date/format-date";
import { DATE_FORMAT } from "../../../../../../../../../constants/common";
import { refetchSchool } from "../../../../../../../../../utils/common";
import { useAcademicYears } from "../../../../../../../../../hooks/useAcademicYear";

const defaultFormValues = {
  name: "",
  start_date: "",
  end_date: "",
  is_active: false,
  is_last_term: false,
};

const AddEditTerms = ({ open, onClose, data }) => {
  const [loading, setLoading] = useState(false);
  const { control, getValues, reset, trigger } = useForm({
    mode: "onChange",
    defaultValues: defaultFormValues,
  });
  const { yearList, selectedYear } = useAcademicYears();

  const isStartDatePast = data?.start_date
    ? dayjs(data?.start_date, DATE_FORMAT.DD_MM_YYYY).isBefore(dayjs(), "day")
    : false;

  useEffect(() => {
    if (selectedYear) {
      reset({
        ...defaultFormValues,
        academic_year_id: selectedYear,
      });
    }
  }, [selectedYear]);

  useEffect(() => {
    if (data?.id) {
      reset({
        name: data?.name,
        start_date: validateDate(data?.start_date, DATE_FORMAT.DD_MM_YYYY),
        end_date: validateDate(data?.end_date, DATE_FORMAT.DD_MM_YYYY),
        academic_year_id: data?.academic_year_id,
        is_active: data?.is_active,
        is_last_term: data?.is_last_term,
      });
    } else {
      reset(defaultFormValues);
    }
  }, [data]);

  const handleSubmit = async () => {
    try {
      const isValid = await trigger();
      if (!isValid) {
        return;
      }

      const formValues = getValues();
      const payload = {
        name: formValues?.name,
        start_date: dayjs(formValues?.start_date).format(
          DATE_FORMAT.DD_MM_YYYY
        ),
        end_date: dayjs(formValues?.end_date).format(DATE_FORMAT.DD_MM_YYYY),
        academic_year_id: formValues?.academic_year_id,
        is_active: formValues?.is_active,
        is_last_term: formValues?.is_last_term,
      };
      if (data?.id) await updateTerms(data?.id, payload);
      else await createTerms(payload);
      toast.success(`Term ${data?.id ? "edited" : "created"} successfully!`);
      refetchSchool();
      handleClose();
    } catch (err) {
      console.log(err);
      toast.error(
        err?.data?.message || `Failed to ${data?.id ? "edit" : "create"} term`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset(defaultFormValues);
    onClose();
  };

  return (
    <CustomDrawer
      title={`${data?.id ? "Edit" : "Add New"} Term`}
      open={open}
      onClose={handleClose}
      className={styles.drawer}
      footer={
        <div className={styles.footerWrapper}>
          <CustomButton
            onClick={handleClose}
            title="Back"
            variant="outlined"
            className={styles.footerBackButton}
          />
          <CustomButton
            title={`${data?.id ? "Edit" : "Add New"} Term`}
            size="large"
            onClick={handleSubmit}
          />
        </div>
      }
      width="40vw"
      drawerStyles={{ body: { padding: "50px" } }}
    >
      <Row gutter={[0, 20]}>
        <Col span={24}>
          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomTextfield
                id="name"
                label="Term Name"
                showRequired
                labelColor="#171717"
                placeholder="Enter Term Name"
                size="large"
                value={value}
                onChange={onChange}
                status={error ? "error" : ""}
              />
            )}
          />
        </Col>

        <Col span={24}>
          <Controller
            name="academic_year_id"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomSelect
                id="academic_year_id"
                label="Academic Year"
                showRequired
                labelColor="#171717"
                placeholder="--Select year--"
                size="large"
                value={value}
                onChange={onChange}
                status={error ? "error" : ""}
                disabled
              >
                {yearList?.map((item) => (
                  <Select.Option key={item?.id} value={item?.id}>
                    {item?.name}
                  </Select.Option>
                ))}
              </CustomSelect>
            )}
          />
        </Col>

        <Col span={24} className={styles.dateRangeWrapper}>
          <Row gutter={[12, 0]} align="bottom">
            <Col span={11} className={styles.dateInput}>
              <Controller
                name="start_date"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomDatepicker
                    id="start_date"
                    label="Term Duration"
                    showRequired
                    labelColor="#171717"
                    placeholder="Start Date"
                    size="large"
                    allowClear={false}
                    value={value}
                    onChange={onChange}
                    disabled={isStartDatePast}
                    style={{ height: "40px", width: "100%" }}
                    status={error ? "error" : ""}
                    prefix={
                      <img
                        src="/assets/images/calendarOutlined.svg"
                        style={{ marginRight: 4 }}
                        alt="calendar"
                      />
                    }
                  />
                )}
              />
            </Col>
            <Col span={2} className={styles.dateArrow}>
              <ArrowRightOutlined style={{ fontSize: 18 }} />
            </Col>
            <Col span={11} className={styles.dateInput}>
              <Controller
                name="end_date"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomDatepicker
                    id="end_date"
                    labelColor="#171717"
                    placeholder="End Date"
                    size="large"
                    allowClear={false}
                    value={value}
                    onChange={onChange}
                    style={{ height: "40px", width: "100%" }}
                    status={error ? "error" : ""}
                    prefix={
                      <img
                        src="/assets/images/calendarOutlined.svg"
                        style={{ marginRight: 4 }}
                        alt="calendar"
                      />
                    }
                  />
                )}
              />
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <label
            style={{
              fontWeight: 500,
              margin: "12px 0",
              display: "inline-block",
            }}
          >
            Select applicable options for this term:
          </label>
          <Controller
            name="is_active"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomCheckBox
                id="is_active"
                label="Active"
                showOptional="(Mark if the term being created is the current or active term)"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                errorMessage={error?.message}
              />
            )}
          />
          <Controller
            name="is_last_term"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomCheckBox
                id="is_last_term"
                label="Last Term"
                showOptional="(Mark if the term being created is the last term)"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                errorMessage={error?.message}
              />
            )}
          />
        </Col>
      </Row>
    </CustomDrawer>
  );
};

export default AddEditTerms;
